import { DotLegalBreadCrumbModel } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useTranslation } from "../../localization/useTranslation";
import { useUrlProvider } from "../../useUrlProvider";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import useLegalEntityAuditGroupDraftOverviewHooks from "./LegalEntityAuditGroupDraftOverview.hooks";
import AuditGroupLegalEntitySelector from "./auditGroupLegalEntitySelector/AuditGroupLegalEntitySelector";
import AuditGroupHeader from "../legalEntityAuditGroupOverview/auditGroupHeader/AuditGroupHeader";
import QuestionnaireTemplateLink from "../../legalEntity/legalEntityAuditTab/QuestionnaireTemplateLink.tsx";
import DotLegalLink from "../../common/components/dotLegalLink/DotLegalLink.tsx";
import { usePlanContext } from "../../auth/planProvider/PlanProvider.tsx";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";
import EntityDetailTopBar from "@common/components/entityDetailTopBar/EntityDetailTopBar";
import TopBarRow from "@common/components/entityDetailTopBar/TopBarRow";
import TopBarTextTile from "@common/components/entityDetailTopBar/TopBarTextTile";
import { Box } from "@mui/material";

function LegalEntityAuditGroupDraftOverview() {
    const { customerName } = useUserContext();
    const { translateString, translateDate } = useTranslation();
    const { getAuditGroupsUrl, getAuditGroupUrl, getGroupEntityUrl } = useUrlProvider();
    const { vendorManagementFeatures } = usePlanContext();

    const { id, data, isLoading, onAuditGroupUpdated, updateSelectedLegalEntities, refetch } = useLegalEntityAuditGroupDraftOverviewHooks();

    const getBreadCrumbs = () => {
        let breadCrumbs: Array<DotLegalBreadCrumbModel> = [];

        breadCrumbs.push({ name: translateString("externalAudits"), link: getAuditGroupsUrl() });
        breadCrumbs.push({
            name: data?.name,
            link: data?.sent ? getAuditGroupUrl(id!) : undefined,
            tooltip: isNullOrWhitespace(data?.description) ? undefined : <MentionInputRender value={data?.description ?? ""} linkColor={"white"} />,
        });

        if (data && data.sent !== null) {
            breadCrumbs.push({ name: translateString("addLegalEntity") });
        }

        return breadCrumbs;
    };

    return (
        <React.Fragment>
            <AuditGroupHeader
                breadCrumbs={getBreadCrumbs()}
                customerName={customerName}
                onAuditGroupUpdated={onAuditGroupUpdated}
                auditGroupModel={
                    data
                        ? {
                              id: data.id,
                              auditTemplateId: data!.auditTemplateId,
                              auditTemplateName: data.auditTemplate,
                              auditTemplateVersion: data.auditTemplateVersion,
                              deadline: data.deadline,
                              name: data.name,
                              responsibleId: data.responsibleId,
                              description: data.description,
                              senderId: data.senderId,
                              sender: data.sender,
                              sent: data.sent,
                              questionnaireIntroductionTitle: data.questionnaireIntroductionTitle,
                          }
                        : undefined
                }
                showAddLegalEntityBtn={false}
                canDeleteAuditgroup={data !== undefined && data.sent === null}
                showDeleteMenuItem={data !== undefined && data.sent === null}
            />
            <Box sx={{ mb: 2 }}>
                <EntityDetailTopBar>
                    <TopBarRow>
                        <TopBarTextTile
                            label={translateString("status")}
                            isLoading={isLoading}
                            value={data?.sent ? translateDate(data.sent) : translateString("draft")}
                        />
                        <TopBarTextTile
                            label={translateString("auditTemplate")}
                            isLoading={isLoading}
                            value={
                                data ? (
                                    vendorManagementFeatures.auditGroups ? (
                                        <QuestionnaireTemplateLink id={data.auditTemplateId} name={data.auditTemplate} type={"audit"} />
                                    ) : (
                                        data.auditTemplate
                                    )
                                ) : (
                                    ""
                                )
                            }
                        />
                        <TopBarTextTile label={translateString("deadline")} isLoading={isLoading} value={data ? translateDate(data.deadline) : ""} />
                        <TopBarTextTile label={translateString("responsible")} isLoading={isLoading} value={data ? data.responsible : ""} />
                        <TopBarTextTile
                            label={translateString("sentFrom")}
                            isLoading={isLoading}
                            value={
                                data ? (
                                    data.sender ? (
                                        <DotLegalLink bold linkColor={"primary"} to={getGroupEntityUrl(data.senderId)}>
                                            {data.sender}
                                        </DotLegalLink>
                                    ) : (
                                        customerName
                                    )
                                ) : (
                                    ""
                                )
                            }
                        />
                    </TopBarRow>
                </EntityDetailTopBar>
            </Box>
            <AuditGroupLegalEntitySelector
                selectedEntities={data?.selectedEntities ?? []}
                onSelectedEntitiesUpdated={updateSelectedLegalEntities}
                selectedEntititesIsLoading={isLoading}
                rows={data?.rows ?? []}
                onContactCreated={() => refetch()}
                auditGroupId={id!}
                auditGroupName={data?.name ?? ""}
                isDeadlineOverdue={data?.isDeadlineOverdue ?? false}
            />
        </React.Fragment>
    );
}

export default LegalEntityAuditGroupDraftOverview;
